import { Row, Table } from '@tanstack/react-table';
import { VirtualItem, useVirtual } from 'react-virtual';
import { CSSProp } from 'styled-components';

import { RefObject, useEffect } from 'react';
import TableRow from './Row';
import { TPadding } from './common';
import { calculateSpacing } from './helpers';
import { RowIndexProvider, useRowIndex } from './RowIndexProvider';

interface TableBodyProps<T> {
  isExpandable: boolean;
  trCss?: (row: Row<T>, index: number) => CSSProp;
  getRowTestId?: (row: T, index: number) => string;
  isMobile: boolean;
  isVirtualized: boolean;
  onSelectedRowsChange?: (selected: readonly T[]) => void;
  table: Table<T>;
  tableContainerRef: RefObject<HTMLDivElement>;
  overScanRowCount: number;
}

export default function TableBody<T>({
  isExpandable,
  trCss,
  getRowTestId,
  isMobile,
  isVirtualized,
  onSelectedRowsChange,
  table,
  tableContainerRef,
  overScanRowCount,
  ...rest
}: TableBodyProps<T>) {
  const shouldNotVirtualize = !isVirtualized;

  useEffect(() => {
    const rowsData = table.getSelectedRowModel().rows.map((r) => r.original);
    onSelectedRowsChange?.(rowsData);
  }, [onSelectedRowsChange, table]);

  const { rows } = table.getRowModel();
  const virtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: overScanRowCount,
  });
  const rowVirtualizer = !shouldNotVirtualize ? virtualizer : null;
  const { virtualItems: virtualRows, totalSize } = rowVirtualizer || {
    virtualItems: [],
    totalSize: 0,
  };
  const hasVirtualRows = Boolean(
    isVirtualized && virtualRows.length && virtualRows.length > 0,
  );
  const paddingTop = hasVirtualRows ? virtualRows[0]?.start ?? 0 : 0;
  const paddingBottom = hasVirtualRows
    ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0)
    : 0;
  const iterateRows = virtualRows.length !== 0 ? virtualRows : rows;

  const spacingTable = calculateSpacing(virtualRows, tableContainerRef);

  return (
    <tbody {...rest}>
      <tr hidden={isMobile || !isVirtualized}>
        <TPadding height={paddingTop} />
      </tr>
      {iterateRows.map((row, index) => (
        <RowIndexProvider key={row.index} value={index}>
          <RowVirtualizer<T>
            row={rows[index]!}
            virtualRow={virtualRows[row.index]}
            isExpandable={isExpandable}
            rows={rows}
            trCss={trCss}
            getRowTestId={getRowTestId}
          />
        </RowIndexProvider>
      ))}
      <tr hidden={isMobile || !isVirtualized}>
        <TPadding height={paddingBottom + spacingTable} />
      </tr>
    </tbody>
  );
}

function RowVirtualizer<T>({
  virtualRow,
  isExpandable,
  row,
  rows,
  trCss,
  getRowTestId,
}: {
  virtualRow?: VirtualItem;
  rows: Row<T>[];
  row: Row<T>;
  isExpandable: boolean;
  trCss?: (row: Row<T>, index: number) => CSSProp;
  getRowTestId?: (row: T, index: number) => string;
}) {
  const virtualRowCOntent =
    virtualRow?.index !== undefined ? rows[virtualRow.index] : null;
  const finalRow = virtualRowCOntent ?? row;
  const currentIndex = useRowIndex();
  const label = finalRow.getIsGrouped() ? 'group' : 'row';
  return (
    <TableRow
      isExpandable={isExpandable}
      key={finalRow.id}
      row={finalRow}
      makeStyle={trCss}
      dataTestId={
        finalRow.getIsGrouped()
          ? `table-row-grouped-${finalRow.index}`
          : getRowTestId?.(finalRow.original, currentIndex) ??
            `table-row-${finalRow.index}`
      }
      aria-label={label}
    />
  );
}
